import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, MapPin, Loader2, ArrowLeft, Share2 } from 'lucide-react';
import SEO from './SEO';
import SectionTitle from './SectionTitle';
import Button from './Button';
import config from '../utils/config';

const KaryaDetailPage = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${config.url}/events`);
        if (!response.ok) throw new Error('Failed to fetch event');
        const data = await response.json();
        const found = data.find(e => String(e.id) === String(id));
        if (!found) throw new Error('कार्य नहीं मिला');
        setEvent(found);
      } catch (err) {
        setError('कार्य डेटा लोड करने में समस्या आई है।');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('hi-IN', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  if (loading) return (
    <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
      <div className="container mx-auto px-4 text-center">
        <SectionTitle>कार्य विवरण</SectionTitle>
        <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
        <p className="text-lg text-gray-600">कार्य लोड हो रहा है...</p>
      </div>
    </section>
  );
  if (error || !event) return (
    <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
      <div className="container mx-auto px-4 text-center">
        <SectionTitle>कार्य विवरण</SectionTitle>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-md mx-auto">
          <p className="text-lg">{error || 'कार्य नहीं मिला'}</p>
        </div>
        <Button to="/karya" size="sm" variant="outline" leftIcon={ArrowLeft} className="mt-8">
          सभी कार्य पर वापस जाएं
        </Button>
      </div>
    </section>
  );

  return (
    <React.Fragment>
      <SEO title={event.title} description={event.description} />
      <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="mb-10">
            <SectionTitle>कार्य विवरण</SectionTitle>
          </div>
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col md:flex-row">
            {event.image_url && (
              <div className="md:w-1/2 bg-gray-100 flex items-center justify-center p-6">
                <img
                  src={`${config.url}${event.image_url}`}
                  alt={event.title}
                  className="rounded-xl shadow-lg w-full max-w-md object-cover"
                  onError={e => { e.target.src = 'https://via.placeholder.com/400x300?text=कार्य+छवि'; }}
                />
              </div>
            )}
            <div className="flex-1 p-8 flex flex-col justify-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}>{event.title}</h2>
              <div className="flex items-center text-gray-600 text-base mb-2">
                <Calendar className="w-5 h-5 mr-2 text-orange-500" />
                <span>{formatDate(event.date)}</span>
              </div>
              {event.location && (
                <div className="flex items-center text-gray-600 text-base mb-2">
                  <MapPin className="w-5 h-5 mr-2 text-orange-500" />
                  <span>{event.location}</span>
                </div>
              )}
              {event.description && (
                <p className="text-gray-700 text-lg mt-4 mb-6 whitespace-pre-line leading-relaxed">{event.description}</p>
              )}
              <Button to="/karya" size="sm" variant="outline" leftIcon={ArrowLeft} className="mt-4">
                सभी कार्य पर वापस जाएं
              </Button>
            </div>
          </div>
        </div>
      </section>
    </React.Fragment>
  );
};

export default KaryaDetailPage;